import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiEye, FiEyeOff, FiUser, FiMail, FiPhone, FiMapPin, FiUpload, FiFileText, FiCheck, FiX, FiUsers, FiShoppingBag, FiLock, FiMap } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';

const VendorRegister = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Details
    personalDetails: {
      name: '',
      contactNumber: '',
      email: '',
      identityCard: ''
    },
    // Restaurant Details
    restaurantDetails: {
      restaurantName: '',
      city: '',
      address: '',
      latitude: '',
      longitude: '',
      fssaiNumber: '',
      gstNumber: ''
    },
    // Documents
    documents: {
      gstCertificate: null,
      fssaiCertificate: null,
      restaurantLogo: null,
      tradeLicense: null
    },
    // Owner Credentials
    ownerCredentials: {
      username: '',
      password: '',
      confirmPassword: ''
    }
  });

  const [documentPreviews, setDocumentPreviews] = useState({
    gstCertificate: null,
    fssaiCertificate: null,
    restaurantLogo: null,
    tradeLicense: null
  });

  const fileInputRefs = {
    restaurantLogo: useRef(null),
    fssaiCertificate: useRef(null),
    gstCertificate: useRef(null),
    tradeLicense: useRef(null)
  };

  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    message: ''
  });

  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Check username availability when username changes
    if (section === 'ownerCredentials' && field === 'username') {
      checkUsernameAvailability(value);
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 4) {
      setUsernameStatus({ checking: false, available: null, message: '' });
      return;
    }

    setUsernameStatus({ checking: true, available: null, message: 'Checking...' });

    try {
      const response = await api.get(`/vendor/check-username?username=${username}`);
      setUsernameStatus({
        checking: false,
        available: response.data.available,
        message: response.data.message
      });
    } catch (error) {
      setUsernameStatus({
        checking: false,
        available: null,
        message: 'Error checking username'
      });
    }
  };

  const fetchUsernameSuggestions = async () => {
    const name = formData.personalDetails.name;
    if (!name || name.length < 3) {
      toast.error('Please enter your name first to get username suggestions');
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await api.get(`/vendor/username-suggestions?name=${encodeURIComponent(name)}`);
      setUsernameSuggestions(response.data.suggestions || []);
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        // toast.success('Username suggestions generated!');
      }
    } catch (error) {
      // toast.error('Failed to generate suggestions');
      setUsernameSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectSuggestion = (username) => {
    setFormData(prev => ({
      ...prev,
      ownerCredentials: {
        ...prev.ownerCredentials,
        username
      }
    }));
    checkUsernameAvailability(username);
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [field]: file
        }
      }));
      
      // Create preview for images
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocumentPreviews(prev => ({
          ...prev,
          [field]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (field) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [field]: null
      }
    }));

    setDocumentPreviews(prev => ({
      ...prev,
      [field]: null
    }));

    if (fileInputRefs[field]?.current) {
      fileInputRefs[field].current.value = '';
    }
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        const { name, contactNumber, email } = formData.personalDetails;
        if (!name || !contactNumber || !email) {
          toast.error('Please fill all required personal details');
          return false;
        }
        if (!/^[0-9]{10}$/.test(contactNumber)) {
          toast.error('Please enter valid 10-digit contact number');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          toast.error('Please enter valid email address');
          return false;
        }

        const { password, confirmPassword, username } = formData.ownerCredentials;
        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        if (!username || !password || !confirmPassword) {
          toast.error('Please fill all credential fields');
          return false;
        }
        if (username.length < 4) {
          toast.error('Username must be at least 4 characters');
          return false;
        }
        if (usernameStatus.available === false) {
          toast.error('Username is already taken. Please choose another one.');
          return false;
        }
        return true;

      case 2:
        const { restaurantName, city, address, latitude, longitude } = formData.restaurantDetails;
        if (!restaurantName || !city || !address) {
          toast.error('Please fill all required restaurant details');
          return false;
        }
        if (!latitude || !longitude) {
          toast.error('Restaurant location (latitude and longitude) is required');
          return false;
        }
        if (isNaN(parseFloat(latitude)) || isNaN(parseFloat(longitude))) {
          toast.error('Please enter valid latitude and longitude');
          return false;
        }
        return true;

      case 3:
        return true; // Documents are optional

      // case 4:
      //   const { username, password, confirmPassword } = formData.ownerCredentials;
      //   if (!username || !password || !confirmPassword) {
      //     toast.error('Please fill all credential fields');
      //     return false;
      //   }
      //   if (username.length < 4) {
      //     toast.error('Username must be at least 4 characters');
      //     return false;
      //   }
      //   if (usernameStatus.available === false) {
      //     toast.error('Username is already taken. Please choose another one.');
      //     return false;
      //   }
      //   if (password.length < 6) {
      //     toast.error('Password must be at least 6 characters');
      //     return false;
      //   }
      //   if (password !== confirmPassword) {
      //     toast.error('Passwords do not match');
      //     return false;
      //   }
      //   return true;

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => {
        return prev + 1;
      });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    toast.loading('Getting your location...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude.toFixed(6);
        const longitude = position.coords.longitude.toFixed(6);
        
        setFormData(prev => ({
          ...prev,
          restaurantDetails: {
            ...prev.restaurantDetails,
            latitude,
            longitude
          }
        }));
        
        toast.dismiss();
        toast.success('Location captured successfully!');
      },
      (error) => {
        toast.dismiss();
        let errorMessage = 'Unable to get location';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
        
        toast.error(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    setLoading(true);
    const submitData = new FormData();

    // Append personal details
    Object.keys(formData.personalDetails).forEach(key => {
      if (formData.personalDetails[key]) {
        submitData.append(`personalDetails.${key}`, formData.personalDetails[key]);
      }
    });

    // Append restaurant details (excluding latitude/longitude as they'll be in location)
    Object.keys(formData.restaurantDetails).forEach(key => {
      if (formData.restaurantDetails[key] && key !== 'latitude' && key !== 'longitude') {
        submitData.append(`restaurantDetails.${key}`, formData.restaurantDetails[key]);
      }
    });

    // Append location coordinates
    if (formData.restaurantDetails.latitude && formData.restaurantDetails.longitude) {
      submitData.append('restaurantDetails.latitude', formData.restaurantDetails.latitude);
      submitData.append('restaurantDetails.longitude', formData.restaurantDetails.longitude);
    }

    // Append documents
    Object.keys(formData.documents).forEach(key => {
      if (formData.documents[key]) {
        submitData.append(key, formData.documents[key]);
      }
    });

    // Append owner credentials
    submitData.append('ownerCredentials.username', formData.ownerCredentials.username);
    submitData.append('ownerCredentials.password', formData.ownerCredentials.password);

    try {
      const response = await api.post('/vendor/register', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Registration successful! Please wait for admin approval.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {[1, 2, 3].map(step => (
        <div key={step} className="flex-1 flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
            currentStep >= step 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 text-gray-500'
          }`}>
            {step}
          </div>
          <div className={`ml-3 text-sm font-medium ${
            currentStep >= step ? 'text-blue-600' : 'text-gray-500'
          }`}>
            {step === 1 && 'Personal Details'}
            {step === 2 && 'Restaurant Details'}
            {step === 3 && 'Documents'}
          </div>
          {step < 3 && (
            <div className={`flex-1 h-1 mx-4 ${
              currentStep > step ? 'bg-blue-500' : 'bg-gray-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-[#2596be] to-[#1e7a9e] p-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FiShoppingBag className="text-white text-2xl" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Vendor Registration</h1>
            <p className="text-blue-100">Join HUKAM MERE AAKA as a restaurant partner</p>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {renderStepIndicator()}

          <form onSubmit={handleSubmit} className="space-y-8">
          {/* Step 1: Personal Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Personal Details</h2>
                <p className="text-gray-600">Tell us about yourself</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiUser className="inline mr-2" size={16} />
                    Owner Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.personalDetails.name}
                    onChange={(e) => handleInputChange('personalDetails', 'name', e.target.value)}
                    onBlur={fetchUsernameSuggestions}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiPhone className="inline mr-2" size={16} />
                    Contact Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.personalDetails.contactNumber}
                    onChange={(e) => handleInputChange('personalDetails', 'contactNumber', e.target.value)}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FiMail className="inline mr-2" size={16} />
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.personalDetails.email}
                  onChange={(e) => handleInputChange('personalDetails', 'email', e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                  required
                />
              </div>

              {/* Username Section */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FiUsers className="inline mr-2" size={16} />
                  Username *
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formData.ownerCredentials.username}
                      onChange={(e) => handleInputChange('ownerCredentials', 'username', e.target.value)}
                      placeholder="Choose a unique username"
                      minLength="4"
                      required
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-all outline-none ${
                        usernameStatus.available === false 
                          ? 'border-red-300 focus:border-red-500' 
                          : usernameStatus.available === true 
                          ? 'border-green-300 focus:border-green-500' 
                          : 'border-gray-200 focus:border-[#2596be]'
                      }`}
                    />
                    {usernameStatus.checking && (
                      <p className="mt-1 text-sm text-blue-600">Checking availability...</p>
                    )}
                    {usernameStatus.available === true && (
                      <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                        <FiCheck size={14} /> Username is available
                      </p>
                    )}
                    {usernameStatus.available === false && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <FiX size={14} /> Username is already taken
                      </p>
                    )}
                    {!usernameStatus.checking && usernameStatus.available === null && (
                      <p className="mt-1 text-sm text-gray-500">Minimum 4 characters</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiLock className="inline mr-2" size={16} />
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.ownerCredentials.password}
                      onChange={(e) => handleInputChange('ownerCredentials', 'password', e.target.value)}
                      placeholder="Create a strong password"
                      required
                      minLength={6}
                      className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#2596be] transition-colors p-1"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiLock className="inline mr-2" size={16} />
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.ownerCredentials.confirmPassword}
                      onChange={(e) => handleInputChange('ownerCredentials', 'confirmPassword', e.target.value)}
                      placeholder="Re-enter your password"
                      required
                      minLength={6}
                      className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#2596be] transition-colors p-1"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FiFileText className="inline mr-2" size={16} />
                  Identity Card Number
                </label>
                <input
                  type="text"
                  value={formData.personalDetails.identityCard}
                  onChange={(e) => handleInputChange('personalDetails', 'identityCard', e.target.value)}
                  placeholder="Aadhaar / PAN / Driving License"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                />
              </div>
            </div>
          )}

          {/* Step 2: Restaurant Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Restaurant Details</h2>
                <p className="text-gray-600">Tell us about your restaurant</p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FiShoppingBag className="inline mr-2" size={16} />
                  Restaurant Name *
                </label>
                <input
                  type="text"
                  value={formData.restaurantDetails.restaurantName}
                  onChange={(e) => handleInputChange('restaurantDetails', 'restaurantName', e.target.value)}
                  placeholder="Enter restaurant name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiMapPin className="inline mr-2" size={16} />
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.restaurantDetails.city}
                    onChange={(e) => handleInputChange('restaurantDetails', 'city', e.target.value)}
                    placeholder="City"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiFileText className="inline mr-2" size={16} />
                    FSSAI Number
                  </label>
                  <input
                    type="text"
                    value={formData.restaurantDetails.fssaiNumber}
                    onChange={(e) => handleInputChange('restaurantDetails', 'fssaiNumber', e.target.value)}
                    placeholder="FSSAI License Number"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FiMapPin className="inline mr-2" size={16} />
                  Complete Address *
                </label>
                <textarea
                  value={formData.restaurantDetails.address}
                  onChange={(e) => handleInputChange('restaurantDetails', 'address', e.target.value)}
                  placeholder="Enter complete restaurant address"
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none resize-none"
                  required
                />
              </div>

              {/* Location Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">
                    <FiMap className="inline mr-2" size={16} />
                    Restaurant Location *
                  </label>
                  <button 
                    type="button" 
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                    onClick={getCurrentLocation}
                  >
                    <FiMapPin size={16} />
                    Get Location
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latitude *</label>
                    <input
                      type="text"
                      value={formData.restaurantDetails.latitude}
                      onChange={(e) => handleInputChange('restaurantDetails', 'latitude', e.target.value)}
                      placeholder="19.0760"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitude *</label>
                    <input
                      type="text"
                      value={formData.restaurantDetails.longitude}
                      onChange={(e) => handleInputChange('restaurantDetails', 'longitude', e.target.value)}
                      placeholder="72.8777"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                      required
                    />
                  </div>
                </div>
                
                <p className="text-sm text-gray-500">
                  Click "Get Current Location" to auto-fill or enter manually
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FiFileText className="inline mr-2" size={16} />
                  GST Number
                </label>
                <input
                  type="text"
                  value={formData.restaurantDetails.gstNumber}
                  onChange={(e) => handleInputChange('restaurantDetails', 'gstNumber', e.target.value)}
                  placeholder="GST Registration Number"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                />
              </div>
            </div>
          )}

          {/* Step 3: Documents */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Documents</h2>
                <p className="text-gray-600">Upload relevant documents for faster approval</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Restaurant Logo */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiUpload className="inline mr-2" size={16} />
                    Restaurant Logo
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRefs.restaurantLogo}
                    onChange={(e) => handleFileChange('restaurantLogo', e.target.files[0])}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                  />
                  {documentPreviews.restaurantLogo && (
                    <div className="mt-3 relative">
                      <img 
                        src={documentPreviews.restaurantLogo} 
                        alt="Logo Preview" 
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        onClick={() => handleRemoveFile('restaurantLogo')}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* FSSAI Certificate */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiFileText className="inline mr-2" size={16} />
                    FSSAI Certificate
                  </label>
                  <input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    ref={fileInputRefs.fssaiCertificate}
                    onChange={(e) => handleFileChange('fssaiCertificate', e.target.files[0])}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                  />
                  {documentPreviews.fssaiCertificate && (
                    <div className="mt-3 relative">
                      <img 
                        src={documentPreviews.fssaiCertificate} 
                        alt="FSSAI Preview" 
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        onClick={() => handleRemoveFile('fssaiCertificate')}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* GST Certificate */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiFileText className="inline mr-2" size={16} />
                    GST Certificate
                  </label>
                  <input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    ref={fileInputRefs.gstCertificate}
                    onChange={(e) => handleFileChange('gstCertificate', e.target.files[0])}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                  />
                  {documentPreviews.gstCertificate && (
                    <div className="mt-3 relative">
                      <img 
                        src={documentPreviews.gstCertificate} 
                        alt="GST Preview" 
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        onClick={() => handleRemoveFile('gstCertificate')}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Trade License */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiFileText className="inline mr-2" size={16} />
                    Trade License
                  </label>
                  <input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    ref={fileInputRefs.tradeLicense}
                    onChange={(e) => handleFileChange('tradeLicense', e.target.files[0])}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#2596be] focus:ring-2 focus:ring-[#2596be] focus:ring-opacity-20 transition-all outline-none"
                  />
                  {documentPreviews.tradeLicense && (
                    <div className="mt-3 relative">
                      <img 
                        src={documentPreviews.tradeLicense} 
                        alt="License Preview" 
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        onClick={() => handleRemoveFile('tradeLicense')}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start gap-3">
                  <FiFileText className="text-blue-600 mt-1" size={16} />
                  <div className="text-sm">
                    <p className="text-blue-800 font-medium mb-1">Important Note:</p>
                    <p className="text-blue-600">Your registration will be reviewed by our admin team. You will receive login credentials via email once approved.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-8 border-t border-gray-200">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200"
                disabled={loading}
              >
                Previous
              </button>
            )}
            
            <div className="ml-auto">
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </button>
              )}
            </div>
          </div>
        </form>

        <div className="text-center mt-6">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 font-semibold hover:underline">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  </div>
);
};

export default VendorRegister;
